/**
 * The Report tab of a well servicing job (fieldiq-api#1286, WS-3): the
 * end-of-job well report.
 *
 * LIFECYCLE (the API's): one DRAFT per job, with no number; its content is
 * built live from the stored rows and shown below it as the preview. ISSUING
 * numbers it WSR-NNNN, freezes the content, signs it with the issuer's name and
 * stores the PDF; it needs the job COMPLETED, and the refusal
 * (job_not_completed) is shown as a sentence, never as a success. AN ISSUED
 * REPORT IS READ-ONLY: no edit, delete or issue is offered on it; a revision
 * is a new draft.
 *
 * The PDF opens as a blob through the client (claimBlobTab before the await,
 * so the popup is not blocked). A draft's PDF is stamped DRAFT by the API.
 *
 * Writes are WS_DISPATCH; the other roles read.
 */
import React, { useState } from 'react';
import { FileText, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useCreateWsReport, useDeleteWsReport, useIssueWsReport, useUpdateWsReport, useWsReport, useWsReports } from '../../hooks/useWsString';
import { claimBlobTab, POPUP_BLOCKED_MESSAGE, showBlobInTab } from '../../lib/blob-tab';
import { formatInstant } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { blankToNull, fmtWsNumber, wsHours, wsReportRefusal, wsReportStatusSkin } from '../../lib/well-servicing';
import type { WsJobDetail, WsMeta, WsReport, WsReportContent } from '../../types/well-servicing';
import { WsAiLauncher } from './ai/WsAiBits';
import { WS_INPUT, WS_PRIMARY, WS_SECONDARY, WsBadge, WsEmpty, WsField, WsFormError, WsLoading, WsPanel, WsSpec } from './WsBits';

/** Open a report's PDF in a new tab; a sentence on failure, null on success. */
export async function openWsReportPdf(reportId: string, win: Window | null): Promise<string | null> {
  if (!win) return POPUP_BLOCKED_MESSAGE;
  try {
    const res = await api.wellServicing.reports.pdf(reportId);
    showBlobInTab(win, res.data);
    return null;
  } catch (err) {
    win.close();
    return wsReportRefusal(err, 'The PDF could not be made.');
  }
}

/** What the report holds (a draft's live preview or the issued content), as the API counted it. */
function ReportContentSummary({ content }: { content: WsReportContent }) {
  const hours = Object.entries(content.hoursByBillingClass ?? {});
  const left = content.stringAsLeft;
  return (
    <div className="space-y-3 border-t border-fieldiq-border px-4 py-4" data-testid="ws-report-content">
      <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <WsSpec label="Tour sheets" value={fmtWsNumber(content.tourCounts?.total)} mono />
        <WsSpec label="Approved" value={fmtWsNumber(content.tourCounts?.approved)} mono />
        <WsSpec label="String events" value={fmtWsNumber(content.stringEvents?.length)} mono />
        <WsSpec label="Voided events" value={fmtWsNumber(content.voidedStringEvents)} mono />
        <WsSpec label="Tallies" value={fmtWsNumber(content.tallies?.length)} mono />
        <WsSpec label="Pressure tests" value={fmtWsNumber(content.pressureTests?.length)} mono />
        <WsSpec label="Drills" value={fmtWsNumber(content.drills?.length)} mono />
        <WsSpec label="Installed lots as left" value={left ? fmtWsNumber(left.installed?.length) : '—'} mono />
      </dl>
      {hours.length > 0 && (
        <p className="text-sm text-fieldiq-text">
          <span className="font-bold">Hours: </span>
          {hours.map(([k, h]) => `${k.replace(/_/g, ' ')} ${wsHours(h)}`).join(' · ')}
        </p>
      )}
      {content.tourCounts && content.tourCounts.notApproved > 0 && (
        <p className="text-sm text-fi-amber">
          {content.tourCounts.notApproved} tour {content.tourCounts.notApproved === 1 ? 'sheet is' : 'sheets are'} not approved by the office yet.
        </p>
      )}
      {left && !left.baselineKnown && (
        <p className="text-sm text-fi-amber" data-testid="ws-report-baseline-unknown">
          The string as left is incomplete: a pull took more than the record shows was ever run, so what was in the well before is not known.
        </p>
      )}
    </div>
  );
}

function ReportCard({ report, job, meta, canDispatch }: { report: WsReport; job: WsJobDetail; meta: WsMeta | null; canDispatch: boolean }) {
  const detail = useWsReport(report.id);
  const update = useUpdateWsReport();
  const issue = useIssueWsReport();
  const del = useDeleteWsReport();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [comments, setComments] = useState('');
  const [summary, setSummary] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [confirming, setConfirming] = useState<'issue' | 'delete' | null>(null);
  // An issued report is read-only: every write below is gated on `draft`.
  const draft = report.status === 'draft';
  const writable = draft && canDispatch;
  const tz = meta?.timeZone ?? null;
  const content = detail.data?.data?.content ?? detail.data?.data?.preview ?? null;
  const failure = queryLoadFailure(detail);

  const openPdf = async () => {
    setError(null);
    const win = claimBlobTab();
    setPdfBusy(true);
    const problem = await openWsReportPdf(report.id, win);
    setPdfBusy(false);
    if (problem) setError(problem);
  };

  return (
    <WsPanel
      eyebrow={draft ? 'Draft report' : 'Issued report'}
      title={report.reportNumber || report.title || 'End-of-job well report'}
      testId="ws-report"
      actions={
        <div className="flex flex-wrap gap-2">
          <button type="button" className={WS_SECONDARY} onClick={openPdf} disabled={pdfBusy} data-testid="ws-report-pdf">
            <FileText className="size-4" aria-hidden="true" />
            {pdfBusy ? 'Opening…' : draft ? 'Preview PDF' : 'Download PDF'}
          </button>
          {writable && !editing && (
            <>
              <button
                type="button"
                className={WS_SECONDARY}
                data-testid="ws-report-edit"
                onClick={() => {
                  setError(null);
                  setTitle(report.title ?? '');
                  setComments(report.comments ?? '');
                  setSummary(report.summary ?? '');
                  setEditing(true);
                }}
              >
                Edit
              </button>
              <button type="button" className={WS_SECONDARY} onClick={() => { setError(null); setConfirming('delete'); }}>
                Delete draft
              </button>
              <button type="button" className={WS_PRIMARY} onClick={() => { setError(null); setConfirming('issue'); }} data-testid="ws-issue-report">
                Issue
              </button>
            </>
          )}
        </div>
      }
    >
      <div className="space-y-3 px-4 py-4" data-status={report.status}>
        <WsFormError message={error} />
        {draft && job.status !== 'completed' && (
          <p className="text-sm text-fieldiq-muted">The job is not completed yet. The report can be drafted and previewed now, and issued once the job is completed.</p>
        )}
        {!draft && (
          <p className="text-sm text-fieldiq-muted" data-testid="ws-report-issued-note">
            Issued reports cannot be changed. Start a new draft for a revision.
          </p>
        )}
        {confirming && writable && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark/40 px-3 py-2" data-testid="ws-report-confirm">
            <p className="text-sm text-fieldiq-text">
              {confirming === 'issue'
                ? 'Issue this report? It is numbered, signed with your name and can no longer be changed.'
                : 'Delete this draft? Its content is rebuilt from the job if you start a new one.'}
            </p>
            <div className="flex gap-2">
              <button type="button" className={WS_SECONDARY} onClick={() => setConfirming(null)} disabled={issue.isPending || del.isPending}>
                Not now
              </button>
              <button
                type="button"
                className={WS_PRIMARY}
                disabled={issue.isPending || del.isPending}
                data-testid={confirming === 'issue' ? 'ws-issue-confirm' : 'ws-delete-confirm'}
                onClick={() => {
                  setError(null);
                  if (confirming === 'issue') {
                    issue.mutate(report.id, {
                      onSuccess: (res) => {
                        toast.success(res.data?.reportNumber ? `Report ${res.data.reportNumber} issued` : 'Report issued');
                        setConfirming(null);
                      },
                      onError: (err) => {
                        setConfirming(null);
                        setError(wsReportRefusal(err, 'The report could not be issued.'));
                      },
                    });
                  } else {
                    del.mutate(report.id, {
                      onSuccess: () => toast.success('Draft deleted'),
                      onError: (err) => {
                        setConfirming(null);
                        setError(wsReportRefusal(err, 'The draft could not be deleted.'));
                      },
                    });
                  }
                }}
              >
                {confirming === 'issue' ? (issue.isPending ? 'Issuing…' : 'Issue report') : del.isPending ? 'Deleting…' : 'Delete draft'}
              </button>
            </div>
          </div>
        )}
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <WsSpec label="Report" value={report.reportNumber || 'Not numbered until issued'} mono />
          <WsSpec label="Status" value={<WsBadge skin={wsReportStatusSkin(report.status)} value={report.status} />} />
          <WsSpec label="Issued" value={formatInstant(report.issuedAt, tz) ?? '—'} />
          <WsSpec label="Signed by" value={report.issuedSignatoryName || '—'} />
          <WsSpec label="Title" value={report.title || '—'} />
          <WsSpec label="Created" value={formatInstant(report.createdAt, tz) ?? '—'} />
        </dl>
        {editing && writable ? (
          <form
            className="space-y-3"
            data-testid="ws-report-form"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              update.mutate(
                { id: report.id, body: { title: blankToNull(title), comments: blankToNull(comments), summary: blankToNull(summary) } },
                {
                  onSuccess: () => {
                    toast.success('Draft saved');
                    setEditing(false);
                  },
                  onError: (err) => setError(wsReportRefusal(err, 'The draft could not be saved.')),
                },
              );
            }}
          >
            <WsField label="Title">
              <input className={WS_INPUT} aria-label="Report title" value={title} onChange={(e) => setTitle(e.target.value)} />
            </WsField>
            <WsField label="Summary">
              <textarea className={`${WS_INPUT} h-24 py-2`} aria-label="Report summary" value={summary} onChange={(e) => setSummary(e.target.value)} />
            </WsField>
            <WsField label="Comments">
              <textarea className={`${WS_INPUT} h-24 py-2`} aria-label="Report comments" value={comments} onChange={(e) => setComments(e.target.value)} />
            </WsField>
            <div className="flex justify-end gap-2">
              <button type="button" className={WS_SECONDARY} onClick={() => setEditing(false)} disabled={update.isPending}>
                Cancel
              </button>
              <button type="submit" className={WS_PRIMARY} disabled={update.isPending}>
                {update.isPending ? 'Saving…' : 'Save draft'}
              </button>
            </div>
          </form>
        ) : (
          <>
            {report.summary && (
              <p className="whitespace-pre-wrap text-sm text-fieldiq-text">
                <span className="font-bold">Summary: </span>
                {report.summary}
              </p>
            )}
            {report.comments && (
              <p className="whitespace-pre-wrap text-sm text-fieldiq-text">
                <span className="font-bold">Comments: </span>
                {report.comments}
              </p>
            )}
          </>
        )}
      </div>
      {detail.isLoading ? (
        <WsLoading label="Loading the report" />
      ) : failure ? (
        <LoadFailureNotice error={queryFailureError(detail)} kind={failure} subject="the report" />
      ) : content ? (
        <ReportContentSummary content={content} />
      ) : null}
    </WsPanel>
  );
}

/** `canAi`: the caller's role may run the AI drafts (canUseWsAi) — the job page passes it; the report draft launcher shows only then. */
export function ReportsSection({ job, meta, canDispatch, canAi = false }: { job: WsJobDetail; meta: WsMeta | null; canDispatch: boolean; canAi?: boolean }) {
  const query = useWsReports(job.id);
  const create = useCreateWsReport();
  const [error, setError] = useState<string | null>(null);
  const reports = query.data?.data ?? [];
  const hasDraft = reports.some((r) => r.status === 'draft');
  const canCreate = canDispatch && !hasDraft && job.status !== 'draft' && job.status !== 'cancelled';
  const failure = queryLoadFailure(query);

  return (
    <>
      <WsPanel
        eyebrow="Report"
        title="End-of-job well report"
        testId="ws-reports"
        actions={
          canCreate ? (
            <button
              type="button"
              className={WS_PRIMARY}
              disabled={create.isPending}
              data-testid="ws-new-report"
              onClick={() => {
                setError(null);
                create.mutate(
                  { jobId: job.id, body: {} },
                  {
                    onSuccess: () => toast.success('Draft report started'),
                    onError: (err) => setError(wsReportRefusal(err, 'The draft could not be started.')),
                  },
                );
              }}
            >
              <Plus className="size-4" aria-hidden="true" />
              {create.isPending ? 'Starting…' : 'New draft report'}
            </button>
          ) : undefined
        }
      >
        <div className="space-y-3 px-4 py-4">
          <WsFormError message={error} />
          {query.isLoading ? (
            <WsLoading label="Loading the reports" />
          ) : failure ? (
            <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="the reports" />
          ) : reports.length === 0 ? (
            <WsEmpty>
              {job.status === 'draft' ? 'This job is still a draft, so it has nothing to report yet.' : 'No report has been started for this job.'}
            </WsEmpty>
          ) : (
            <p className="text-sm text-fieldiq-muted">
              {reports.length} report{reports.length === 1 ? '' : 's'} · {reports.filter((r) => r.status === 'issued').length} issued
            </p>
          )}
        </div>
      </WsPanel>
      {reports.map((r) => (
        <React.Fragment key={r.id}>
          <ReportCard report={r} job={job} meta={meta} canDispatch={canDispatch} />
          {/* WS-6: the AI drafts the narrative of a DRAFT report only (the API answers 409 report_issued otherwise). */}
          {canAi && r.status === 'draft' && (
            <WsAiLauncher
              request={{ agent: 'ws_well_report_draft', reportId: r.id }}
              eyebrow="AI draft"
              title="Report summary"
              blurb="Drafts the narrative of this draft report from its own figures. Approving writes it into the report's summary, only while the report is still a draft."
              testId="ws-ai-report-draft"
            />
          )}
        </React.Fragment>
      ))}
    </>
  );
}
